/**
 *
 * Agenda
 *
 */

import React, { memo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { createStructuredSelector } from "reselect";
import { compose } from "redux";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import moment from "utils/moment/momentLocale";

// styles
import "./style.scss";

import { useInjectSaga } from "utils/injectSaga";
import { useInjectReducer } from "utils/injectReducer";
import reducer from "./reducer";
import saga from "./saga";

import { flushState, loadCourses } from "./actions";
import {
  makeSelectLoading,
  makeSelectError,
  makeSelectItemsList,
  makeSelectAppointments,
} from "./selectors";

// antd component
import { Layout } from "antd";
const { Content } = Layout;

// components
import CustomPageHeader from "components/CustomPageHeader";
import TCFCalendar from "components/TCFCalendar";
// inner components
import { AgendaList } from "./components";

// utils
import { DAY_OF_WEEK_OPTIONS } from "utils/helpers/helper.constant";
import _local from "utils/localStorageUtils";

export function Agenda({
  // main data
  courseDetail,
  itemsList,
  appointments,
  firstElem,
  // states
  history,
  error,
  loading,
  // actions
  loadCourses,
  flushState,
}) {
  useInjectReducer({ key: "agenda", reducer });
  useInjectSaga({ key: "agenda", saga });

  const { t, i18n } = useTranslation();

  const now = moment();
  const week = now.isoWeekday();

  const localUser = _local.getLocalUser();
  const location = useLocation();

  const onDate = location?.state?.onDate;

  const getDaysWeek = useCallback(() => DAY_OF_WEEK_OPTIONS(), [i18n?.language]);

  let initOptions = {
    _id: localUser?._id,
    course_week: getDaysWeek()[week - 1]?.value,
    onDate: now.toISOString(),
    timeZone: now.utcOffset(),
  };

  const [selectedDate, setSelectedDate] = useState(
    !!onDate && !location?.state?.needRefresh ? moment(onDate) : now,
  );
  const [optionsData, setOptionsData] = useState(initOptions);
  const [currentCourses, setCurrentCourses] = useState([]);
  const [currentAppointments, setCurrentAppointments] = useState([]);

  // Clearing the state after unmounting a component
  // - work like ComponentDidUpdate
  useEffect(() => {
    (itemsList?.length == 0 || appointments?.length == 0) && loadCourses(optionsData);

    if (!!location?.state?.needRefresh) {
      history.replace({
        ...location,
        state: {
          ...location?.state,
          needRefresh: false,
          course_week: getDaysWeek()[week - 1]?.value,
          onDate: now.toISOString(),
          timeZone: now.utcOffset(),
        },
      });
    }

    return () => {
      // Anything in here is fired on component unmount.
      flushState();
      setCurrentCourses([]);
      setCurrentAppointments([]);
      setSelectedDate(null);
      setOptionsData(initOptions);
      // reset location statest for current component if pathname is not includes an "agenda" pathname
      if (!location?.pathname?.includes("agenda")) {
        history.replace({
          ...location,
          state: {
            ...location?.state,
            course_week: getDaysWeek()[week - 1]?.value,
            onDate: now.toISOString(),
            timeZone: now.utcOffset(),
          },
        });
      }
    };
  }, []);

  useEffect(() => {
    if (
      (!!itemsList && itemsList.length > 0) ||
      (!!appointments && appointments?.length > 0)
    ) {
      setCurrentCourses(itemsList);
      setCurrentAppointments(appointments);
    } else {
      setCurrentCourses([]);
      setCurrentAppointments([]);
    }
  }, [itemsList]);

  // Reload list based on location state of date
  useEffect(() => {
    if (!!onDate && !location?.state?.needRefresh) {
      const onDateObject = moment(onDate);
      const onDateWeekday = onDateObject.isoWeekday();
      changeLoadOptions({
        ...optionsData,
        course_week: getDaysWeek()[onDateWeekday - 1]?.value,
        onDate: onDateObject.toISOString(),
        timeZone: onDateObject.utcOffset(),
      });
    }
  }, [onDate]);

  // Loading data when optionsData is changed
  const changeLoadOptions = useCallback(
    newOption => {
      if (JSON.stringify(optionsData) != JSON.stringify(newOption)) {
        // if date is changed then refresh data
        setCurrentCourses([]);
        setCurrentAppointments([]);
        // set new options for loading list
        const options = {
          ...optionsData,
          ...newOption,
        };
        loadCourses(options);
        setOptionsData(options);
      }
    },
    [optionsData],
  );

  const onCalendarSelect = useCallback(
    value => {
      const date = moment(value);
      setSelectedDate(date);
      // 1 - Monday, 7 - Sunday
      const week2 = value.isoWeekday();
      changeLoadOptions({
        ...optionsData,
        course_week: getDaysWeek()[week2 - 1]?.value,
        onDate: date.toISOString(),
        timeZone: date.utcOffset(),
      });
    },
    [optionsData, location, i18n?.language],
  );

  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>{t("layouts.navigation.agenda")}</title>
        <meta name="description" content="Description of Agenda" />
      </Helmet>
      <Content className="main-content-container agenda-page">
        <CustomPageHeader
          className="agenda-header"
          title={t("layouts.navigation.agenda")}
          subTitle={""}
          subTitlePosition="top"
          extraActions={
            [
              /*  <Button
                loading={loading}
                className="box-new-create-btn"
                icon={<CustomIcons type="absence" />}
                onClick={handelClickCreateAbsence}
              >
                {t("common.newRequest")}
              </Button>, */
            ]
          }
        />

        <div className="agenda-page__content">
          <TCFCalendar
            valueState={selectedDate}
            onSelect={onCalendarSelect}
            showTodayButton
          />

          <AgendaList
            className="agenda-list-wrapper"
            loading={loading}
            selectedDate={moment(selectedDate).toISOString()}
            timeZone={moment(selectedDate).utcOffset()}
            dataSourse={currentCourses}
            appointments={currentAppointments}
          />
        </div>
      </Content>
    </Layout>
  );
}

Agenda.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  loading: makeSelectLoading(),
  error: makeSelectError(),
  itemsList: makeSelectItemsList(),
  appointments: makeSelectAppointments(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushState()),
    loadCourses: options => dispatch(loadCourses(options)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(Agenda);
